import { Parser } from '~/api';
import { Schedule, ScheduleCondition, ScheduleRun } from '~/api/models';
import RequestService from './request';

interface CreateScheduleTask {
    action: string;
    time_offset: number;
    payload?: string;
    conditions?: ScheduleCondition[];
}

interface CreateScheduleRequest {
    name: string;
    cron_minute: string;
    cron_hour: string;
    cron_day_of_week: string;
    cron_day_of_month: string;
    /** IANA identifier the cron fields are read in. Omitted means the scheduler's own clock. */
    timezone?: string;
    conditions?: ScheduleCondition[];
    is_active?: boolean;
    enabled?: boolean;
    // Suggested schedules seed their tasks inline so the schedule is created complete.
    tasks?: CreateScheduleTask[];
}

interface DeleteScheduleRequest {
    id: number;
}

class SchedulesService {
    getAll(req: PaginatableRequest): Promise<Schedule[]> {
        return RequestService.get('/servers/:server/schedules', {
            ...req,
            include: 'tasks',
        }).then(Parser.parse);
    }

    get(): Promise<Schedule> {
        return RequestService.get('/servers/:server/schedules/:schedule', {
            include: 'tasks',
        }).then(Parser.parse);
    }

    getRuns(req: PaginatableRequest): Promise<ScheduleRun[]> {
        return RequestService.get('/servers/:server/schedules/:schedule/runs', {
            ...req,
            include: 'tasks',
        }).then(Parser.parse);
    }

    getActions(): Promise<string[]> {
        return RequestService.get('/servers/:server/schedules/actions').then((response: { data: string[] }) => response.data ?? []);
    }

    create(data: CreateScheduleRequest): Promise<Schedule> {
        return RequestService.post('/servers/:server/schedules', data).then(Parser.parse);
    }

    update(data: CreateScheduleRequest): Promise<Schedule> {
        return RequestService.patch('/servers/:server/schedules/:schedule', data).then(Parser.parse).then(RequestService.updateModelBinding);
    }

    toggle(isActive: boolean): Promise<Schedule> {
        return RequestService.patch('/servers/:server/schedules/:schedule/toggle', { is_active: isActive })
            .then(Parser.parse)
            .then(RequestService.updateModelBinding);
    }

    trigger(): Promise<void> {
        return RequestService.post('/servers/:server/schedules/:schedule/trigger');
    }

    delete(data: DeleteScheduleRequest): Promise<void> {
        return RequestService.delete(`/servers/:server/schedules/${data.id}`);
    }
}

export default new SchedulesService();
